import type { Permission, Role } from '@myrentalmate/api-client';

import { homePathFor } from '../lib/me';

// The one list of what's in the top bar and the sidebar. A page gets a link by being added here.

export interface NavLink {
  to: string;
  label: string;
  /** Hidden without this. For tidiness only: the API enforces permissions on every route. */
  permission?: Permission;
}

/** A top bar link. Choosing it goes to its first link and fills the sidebar with all of them. */
export interface NavSection {
  label: string;
  links: NavLink[];
}

export type Area = 'staff' | 'platform' | 'portal' | 'owner' | 'apply';

/** Opened from the user menu, so it has a sidebar but no top bar link. */
const ACCOUNT: NavSection = {
  label: 'Account',
  links: [{ to: '/account', label: 'My Profile' }],
};

const home = (to: string): NavSection => ({
  label: 'Home',
  links: [{ to, label: 'Overview' }],
});

/** At the bottom of the sidebar in every view, for platform admins only. */
export const SUPERADMIN: NavSection = {
  label: 'Superadmin',
  links: [
    { to: '/platform', label: 'Customers' },
    { to: '/platform/status', label: 'System Status' },
    { to: '/platform/diagnostics', label: 'Diagnostics' },
    { to: '/platform/support', label: 'Support Log' },
    { to: '/platform/settings', label: 'App Settings' },
  ],
};

const AREAS: Record<Area, { sections: NavSection[] }> = {
  staff: {
    sections: [
      {
        label: 'Dashboard',
        links: [
          { to: '/staff', label: 'Overview' },
          { to: '/staff/settings', label: 'Settings', permission: 'org.manage' },
        ],
      },
      {
        label: 'Portfolio',
        links: [
          { to: '/staff/properties', label: 'Properties' },
          { to: '/staff/owners', label: 'Owners', permission: 'owners.read' },
        ],
      },
      {
        label: 'People',
        links: [
          { to: '/staff/residents', label: 'Residents' },
          { to: '/staff/vendors', label: 'Vendors' },
          { to: '/staff/messages', label: 'Messages', permission: 'notices.read' },
        ],
      },
      {
        label: 'Leases',
        links: [
          { to: '/staff/leases', label: 'Leases', permission: 'leases.read' },
          { to: '/staff/payments', label: 'Payments', permission: 'leases.read' },
          { to: '/staff/leases/renewals', label: 'Renewals', permission: 'notices.read' },
        ],
      },
      {
        label: 'Maintenance',
        links: [
          { to: '/staff/maintenance', label: 'Work Orders', permission: 'maintenance.read' },
          {
            to: '/staff/maintenance/inspections',
            label: 'Inspections',
            permission: 'maintenance.read',
          },
          {
            to: '/staff/maintenance/recurring',
            label: 'Recurring Work',
            permission: 'maintenance.read',
          },
          { to: '/staff/maintenance/bills', label: 'Vendor Bills', permission: 'bills.read' },
        ],
      },
      {
        label: 'Compliance',
        links: [
          { to: '/staff/compliance', label: 'Licenses', permission: 'compliance.read' },
          { to: '/staff/compliance/notices', label: 'Notices', permission: 'notices.read' },
          {
            to: '/staff/compliance/violations',
            label: 'Lease Violations',
            permission: 'notices.read',
          },
          { to: '/staff/compliance/cases', label: 'Eviction Cases', permission: 'legal.read' },
          { to: '/staff/compliance/calendar', label: 'Calendar' },
        ],
      },
      {
        label: 'Accounting',
        links: [
          { to: '/staff/accounting/reports', label: 'Reports', permission: 'ledger.read' },
          {
            to: '/staff/accounting/owner-statements',
            label: 'Owner Statements',
            permission: 'ledger.read',
          },
          {
            to: '/staff/accounting/banking',
            label: 'Bank Reconciliation',
            permission: 'ledger.read',
          },
          { to: '/staff/accounting/budgets', label: 'Budgets', permission: 'ledger.read' },
          { to: '/staff/accounting', label: 'Trial Balance', permission: 'ledger.read' },
          {
            to: '/staff/accounting/accounts',
            label: 'Chart of Accounts',
            permission: 'ledger.read',
          },
          { to: '/staff/accounting/closing', label: 'Closing', permission: 'ledger.read' },
        ],
      },
      { label: 'Files', links: [{ to: '/staff/files', label: 'Files & Notes' }] },
    ],
  },
  platform: { sections: [home('/platform')] },
  portal: {
    sections: [
      {
        label: 'Home',
        links: [
          { to: '/portal', label: 'Overview' },
          { to: '/portal/payments', label: 'Payments' },
          { to: '/portal/maintenance', label: 'Maintenance' },
          { to: '/portal/inspections', label: 'Inspections' },
          { to: '/portal/notices', label: 'Notices' },
          { to: '/portal/documents', label: 'Documents' },
        ],
      },
    ],
  },
  owner: {
    sections: [
      {
        label: 'Home',
        links: [
          { to: '/owner', label: 'Overview' },
          { to: '/owner/statements', label: 'Statements' },
          { to: '/owner/distributions', label: 'Distributions' },
        ],
      },
    ],
  },
  apply: { sections: [home('/apply')] },
};

/** The area this role calls home. The account pages sit inside it, wherever they're opened from. */
export function homeArea(role: Role): Area {
  return homePathFor(role).slice(1) as Area;
}

/** The sections this person may see: links they lack permission for go, then any left empty. */
export function sectionsFor(area: Area, permissions: readonly Permission[]): NavSection[] {
  return AREAS[area].sections
    .map((section) => ({
      ...section,
      links: section.links.filter((l) => !l.permission || permissions.includes(l.permission)),
    }))
    .filter((section) => section.links.length > 0);
}

/** The link for this page: the longest match, so /staff/properties/7 is Properties, not /staff. */
export function activeLink(sections: NavSection[], pathname: string): NavLink | undefined {
  return sections
    .flatMap((section) => section.links)
    .filter((link) => pathname === link.to || pathname.startsWith(`${link.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0];
}

/** The section whose links belong in the sidebar for this page. */
export function activeSection(sections: NavSection[], pathname: string): NavSection | undefined {
  const link = activeLink([...sections, ACCOUNT], pathname);
  if (link && ACCOUNT.links.includes(link)) return ACCOUNT;
  return sections.find((section) => link && section.links.includes(link)) ?? sections[0];
}
